import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import { trackCTAClick } from "@/utils/analytics";
import { useEffect, useRef, useState } from "react";


function useParallax() {
  const [offset, setOffset] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    const handleScroll = () => {
      rafRef.current = requestAnimationFrame(() => {
        setOffset(window.scrollY * 0.35);
      });
    };

    // Only enable parallax on non-mobile for performance
    const mql = window.matchMedia("(min-width: 768px)");
    if (mql.matches) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return offset;
}

export default function HeroSection() {
  const parallaxOffset = useParallax();

  return (
    <section 
      className="hero-section relative pt-0 pb-16 sm:py-24 md:py-40 overflow-hidden min-h-[80vh] landscape:min-h-[50vh] sm:min-h-screen flex items-center -mt-4 border-t-0"
      style={{ contain: 'layout paint' }}
    >
      {/* Hero background image with parallax */}
      <div className="absolute inset-0 -top-4">
        <picture>
          <source srcSet="/hero-bg-480-v3.webp" media="(max-width: 480px)" type="image/webp" />
          <source srcSet="/hero-bg-768-v3.webp" media="(max-width: 768px)" type="image/webp" />
          <source srcSet="/hero-bg-1920-v3.webp" media="(min-width: 769px)" type="image/webp" />
          <img 
            src="/hero-bg-1920-v3.webp" 
            alt="Luxury motor yacht cruising at golden hour off Mediterranean coastline"
            className="w-full h-[120%] object-cover object-[center_40%] will-change-transform"
            style={{ transform: `translate3d(0, ${parallaxOffset}px, 0)` }}
            width="1920"
            height="1080"
            fetchPriority="high"
          />
        </picture>
      </div>
      
      {/* Dark overlay — heavy enough for all text legibility */}
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black/70" />
      
      {/* Subtle gold accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(40,45%,58%)/0.3] to-transparent" />
      
      {/* Main content */}
      <div className="container mx-auto px-1 sm:px-4 relative z-10 pt-12 sm:pt-0">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Shield className="h-4 w-4 text-white/80" />
            <span className="text-xs uppercase tracking-[0.2em] text-white/80 font-semibold drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)]">Instant · Data-Driven · For Luxury Vessels 40ft+</span>
          </div>

          <h1 className="text-[2.75rem] xs:text-5xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white mb-4 sm:mb-6 px-0 sm:px-0 leading-tight tracking-tight [text-shadow:_0_2px_12px_rgba(0,0,0,0.8),_0_1px_4px_rgba(0,0,0,0.9)]">
            <span className="block font-light">Private Yacht Valuation</span>
            <span className="text-[hsl(40,60%,72%)] block font-light [text-shadow:_0_2px_12px_rgba(0,0,0,0.8),_0_1px_4px_rgba(0,0,0,0.9)]">&amp; Market Intelligence</span>
          </h1>

          <p className="text-base xs:text-lg sm:text-lg md:text-xl text-white/90 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2 sm:px-4 [text-shadow:_0_1px_6px_rgba(0,0,0,0.7)]">
            Data-backed insights for discerning owners, buyers, and advisors. Our AI-driven analysis delivers a comprehensive market report with regional pricing and discreet sale guidance.
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-6 px-2 sm:px-0 w-full max-w-xl mx-auto">
            <Button 
              asChild 
              size="xl" 
              className="w-full sm:w-auto h-auto min-h-[52px] max-w-[calc(100vw-1rem)] sm:max-w-none bg-primary/90 hover:bg-primary text-primary-foreground font-medium transition-all duration-300 border-0 shadow-lg shadow-primary/10"
            >
              <Link 
                to="/valuation" 
                className="flex items-center justify-center gap-2"
                onClick={() => trackCTAClick('Hero Request Valuation', '/valuation', 'Hero Section')}
              >
                <span className="leading-tight">Request Confidential Valuation</span>
              </Link>
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              asChild
              className="border border-white/30 text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:text-white px-6 py-3 text-base font-medium rounded-md transition-all duration-300 w-full sm:w-auto h-auto min-h-[52px] max-w-[calc(100vw-1rem)] sm:max-w-none"
            >
              <a href="#how-it-works" className="scroll-smooth flex items-center justify-center">
                <span className="leading-tight">How It Works</span>
              </a>
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10 max-w-3xl mx-auto px-4 sm:px-0 mt-12">
            {[
              { title: "Instant", desc: "Your valuation remains private unless you choose to explore sale or acquisition options." },
              { title: "Data-Driven", desc: "Cross-references dealer listings, auction results, and regional market trends." },
              { title: "Luxury Focus", desc: "Purpose-built for yachts 40ft and above across all major categories." }
            ].map((item) => (
              <div 
                key={item.title}
                className="text-center p-4 sm:p-0"
              >
                <div className="text-sm uppercase tracking-[0.15em] font-semibold text-[hsl(40,60%,72%)] mb-2 [text-shadow:_0_1px_6px_rgba(0,0,0,0.7)]">{item.title}</div>
                <div className="text-sm text-white/85 leading-relaxed [text-shadow:_0_1px_4px_rgba(0,0,0,0.6)]">{item.desc}</div>
              </div>
            ))}
          </div>
          
          {/* Promise */}
          <p className="text-xs text-white/60 mt-8 tracking-wide [text-shadow:_0_1px_4px_rgba(0,0,0,0.6)]">No pressure. No obligation. If you request next steps, you may be contacted by a verified partner.</p>
        </div>
      </div>
    </section>
  );
}
